import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { createClient } from 'postchain-client';
import { 
    createKeyStoreInteractor,
    createWeb3ProviderEvmKeyStore,
    createAmount
} from '@chromia/ft4';
import { useAccount } from 'wagmi';
import { ecBrid, blockchainUrls, vaultUrl, isConfigValid } from '../config/chromia';

const ChromiaContext = createContext(null);

// Admin account ID that receives entrance fees (configure this)
const ADMIN_ACCOUNT_ID = import.meta.env.VITE_ADMIN_ACCOUNT_ID || '';
const ENTRY_FEE = 50; // 50 CHR
// CHR token asset ID on economy chain
const CHR_ASSET_ID = import.meta.env.VITE_CHR_ASSET_ID || '9EF73A786A66F435B3B40E72F5E9D85A4B09815997E087C809913E1E7EC686B4';

export const useChromia = () => {
    const context = useContext(ChromiaContext);
    if (!context) {
        throw new Error('useChromia must be used within a ChromiaProvider');
    }
    return context;
};

export const ChromiaProvider = ({ children }) => {
    const { address, isConnected, connector } = useAccount();
    
    // State
    const [ecClient, setEcClient] = useState(null); // Economy chain client
    const [keyStore, setKeyStore] = useState(null);
    const [ftAccount, setFtAccount] = useState(null);
    const [chrBalance, setChrBalance] = useState(0);
    const [isLoadingAccount, setIsLoadingAccount] = useState(false);
    const [isTransferring, setIsTransferring] = useState(false);
    const [error, setError] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);

    // Initialize economy chain client
    useEffect(() => {
        const initClient = async () => {
            if (!ecBrid) {
                console.warn('Economy chain BRID not configured');
                setIsInitialized(true);
                return;
            }
            
            try {
                const client = await createClient({
                    nodeUrlPool: blockchainUrls,
                    blockchainRid: ecBrid,
                });
                setEcClient(client);
                setIsInitialized(true);
                console.log('Economy chain client initialized');
            } catch (err) {
                console.error('Failed to initialize economy chain client:', err);
                setError(err);
                setIsInitialized(true);
            }
        };
        initClient();
    }, []);

    // Initialize keystore when wallet is connected
    useEffect(() => {
        const initKeyStore = async () => {
            if (!isConnected || !connector) {
                setKeyStore(null);
                setFtAccount(null);
                setChrBalance(0);
                return;
            }

            try {
                const provider = await connector.getProvider();
                const evmKeyStore = await createWeb3ProviderEvmKeyStore(provider);
                setKeyStore(evmKeyStore);
                console.log('Keystore initialized');
            } catch (err) {
                console.error('Failed to create keystore:', err);
                setError(err);
            }
        };

        initKeyStore();
    }, [isConnected, connector]);

    // Load FT4 account and balance when keystore is ready
    const loadAccount = useCallback(async () => {
        if (!ecClient || !keyStore) return null;

        setIsLoadingAccount(true);
        setError(null);

        try {
            const interactor = createKeyStoreInteractor(ecClient, keyStore);
            const accounts = await interactor.getAccounts();
            
            if (accounts && accounts.length > 0) {
                const account = accounts[0];
                setFtAccount(account);
                
                // Load CHR balance
                await loadChrBalance(account);
                
                return account;
            }
            setFtAccount(null);
            setChrBalance(0);
            return null;
        } catch (err) {
            console.error('Failed to load account:', err);
            setError(err);
            return null;
        } finally {
            setIsLoadingAccount(false);
        }
    }, [ecClient, keyStore]);

    // Helper to convert Uint8Array to hex string
    const uint8ArrayToHex = (arr) => {
        return Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('').toUpperCase();
    };

    // Load CHR balance for account
    const loadChrBalance = useCallback(async (account) => {
        if (!account) {
            console.log('[Chromia] No account provided for balance loading');
            setChrBalance(0);
            return 0;
        }

        try {
            // Query balance using FT4
            const balancesResult = await account.getBalances();
            console.log('[Chromia] Account balances result:', balancesResult);
            
            // getBalances returns {data: Array, nextCursor: null}
            const balances = balancesResult.data || balancesResult;
            
            if (!Array.isArray(balances)) {
                console.error('[Chromia] Balances is not an array:', balances);
                setChrBalance(0);
                return 0;
            }
            
            // Find CHR balance - compare case-insensitively
            const chrAssetIdUpper = CHR_ASSET_ID.toUpperCase();
            const chrBalanceEntry = balances.find(b => {
                // Convert Uint8Array to hex string for comparison
                const assetIdHex = b.asset.id instanceof Uint8Array 
                    ? uint8ArrayToHex(b.asset.id)
                    : b.asset.id.toString().toUpperCase();
                const symbol = b.asset.symbol?.toUpperCase();
                console.log('[Chromia] Checking asset:', { assetIdHex, symbol, name: b.asset.name });
                // Match by asset ID or by symbol (CHR or tCHR for testnet)
                return assetIdHex === chrAssetIdUpper || symbol === 'CHR' || symbol === 'TCHR';
            });
            
            if (chrBalanceEntry) {
                // Amount is an object with value (BigInt) and decimals
                const rawAmount = chrBalanceEntry.amount.value !== undefined 
                    ? Number(chrBalanceEntry.amount.value) 
                    : Number(chrBalanceEntry.amount);
                const decimals = chrBalanceEntry.amount.decimals || chrBalanceEntry.asset.decimals || 6;
                const balance = rawAmount / Math.pow(10, decimals);
                console.log('[Chromia] CHR balance found:', { rawAmount, decimals, balance });
                setChrBalance(balance);
                return balance;
            } else {
                console.log('[Chromia] No CHR balance found, available assets:', balances.map(b => b.asset.symbol));
                setChrBalance(0);
                return 0;
            }
        } catch (err) {
            console.error('[Chromia] Failed to load CHR balance:', err);
            setChrBalance(0);
            return 0;
        }
    }, []);

    // Auto-load account when keystore is ready
    useEffect(() => {
        if (keyStore && ecClient) {
            loadAccount();
        }
    }, [keyStore, ecClient, loadAccount]);

    // Clear account when wallet disconnects
    useEffect(() => {
        if (!isConnected) {
            setFtAccount(null);
            setKeyStore(null);
            setChrBalance(0);
        }
    }, [isConnected]);

    // Pay entry fee (50 CHR to admin)
    const payEntryFee = useCallback(async () => {
        if (!ecClient) {
            throw new Error('Chromia client not initialized');
        }
        
        if (!isConnected || !connector) {
            throw new Error('Wallet not connected');
        }
        
        if (!ftAccount) {
            throw new Error('FT4 account not loaded. Please ensure your wallet is connected and has an FT4 account.');
        }
        
        if (!ADMIN_ACCOUNT_ID) {
            throw new Error('Admin account not configured');
        }

        if (chrBalance < ENTRY_FEE) {
            throw new Error(`Insufficient balance. Need ${ENTRY_FEE} CHR, have ${chrBalance} CHR`);
        }

        setIsTransferring(true);
        setError(null);

        try {
            // Get a fresh provider and keystore to ensure signing works
            console.log('[Chromia] Getting fresh provider for transfer...');
            const provider = await connector.getProvider();
            const freshKeyStore = await createWeb3ProviderEvmKeyStore(provider);
            
            // Create a session for the account to perform the transfer
            const interactor = createKeyStoreInteractor(ecClient, freshKeyStore);
            const session = await interactor.getSession(ftAccount.id);
            
            if (!session) {
                throw new Error('Failed to create session for transfer');
            }
            
            console.log('[Chromia] Session created, initiating transfer...');
            
            // Transfer 50 CHR to admin account
            const amount = createAmount(ENTRY_FEE, 6); // Convert to smallest unit (6 decimals)
            
            // Use session to transfer assets
            await session.account.transfer(
                ADMIN_ACCOUNT_ID,
                CHR_ASSET_ID,
                amount
            );

            // Reload balance after transfer
            await loadChrBalance(ftAccount);
            
            console.log(`Entry fee of ${ENTRY_FEE} CHR paid successfully`);
            return true;
        } catch (err) {
            console.error('Entry fee payment failed:', err);
            setError(err);
            throw err;
        } finally {
            setIsTransferring(false);
        }
    }, [ecClient, isConnected, connector, ftAccount, chrBalance, loadChrBalance]);

    // Request cash out (called by game server with amount)
    const requestCashOut = useCallback(async (amount) => {
        // Convert account ID to hex string for server
        let accountIdHex = '';
        if (ftAccount?.id) {
            if (ftAccount.id instanceof Uint8Array) {
                accountIdHex = uint8ArrayToHex(ftAccount.id);
            } else if (Buffer.isBuffer(ftAccount.id)) {
                accountIdHex = ftAccount.id.toString('hex').toUpperCase();
            } else {
                accountIdHex = String(ftAccount.id);
            }
        }
        
        return {
            accountId: accountIdHex,
            amount,
            address: address
        };
    }, [ftAccount, address]);

    // Refresh balance
    const refreshBalance = useCallback(async () => {
        if (ftAccount) {
            await loadChrBalance(ftAccount);
        }
    }, [ftAccount, loadChrBalance]);

    // Check if user can pay entry
    const canPayEntry = ftAccount && chrBalance >= ENTRY_FEE && !isTransferring;
    const hasAccount = !!ftAccount;

    const value = {
        // Connection state
        ecClient,
        keyStore,
        isInitialized,
        
        // Account state
        ftAccount,
        hasAccount,
        isLoadingAccount,
        chrBalance,
        
        // Entry fee
        entryFee: ENTRY_FEE,
        canPayEntry,
        payEntryFee,
        
        // Transfer state
        isTransferring,
        
        // Cash out
        requestCashOut,
        
        // Actions
        loadAccount,
        refreshBalance,
        
        // Error handling
        error,
        setError,
        
        // Config
        blockchainUrls,
        ecBrid,
        vaultUrl,
        isConfigValid,
        adminAccountId: ADMIN_ACCOUNT_ID,
    };

    return (
        <ChromiaContext.Provider value={value}>
            {children}
        </ChromiaContext.Provider>
    );
};

export default ChromiaProvider;
